// Calculated fields that produce TEXT, and number operands that read a column as text.
//
// Mirrors ReportSqlBuilder.TextCalculatedFields.cs - keep the two identical (the server binds
// typed text as parameters where this preview writes N'...' literals; everything else,
// including the integers written into the SQL, is the same).
//
// A text field is up to MAX_TEXT_PARTS pieces. Each piece starts from a column (any visible,
// non-sensitive column, read as text) or typed text, and runs up to MAX_TEXT_STEPS string
// steps in order. Pieces are joined with the optional separator between non-blank pieces; an
// empty result is blank. Text fields are Full Row Detail columns only.
//
// SQL rules (see the C# file for the reasoning):
//   - Everything is NVARCHAR(4000); dates read as text use style 120, times of day style 108.
//   - SQL Server 2016 functions only. Lengths use DATALENGTH(...) / 2 (LEN drops trailing spaces).
//   - A step that reads its input more than once is wrapped as
//     (SELECT <step on rc_v> FROM (VALUES (<input>)) AS rc_t(rc_v)) so the input appears once.
//   - Matching (replace, before, after, part, position) follows the database collation, which
//     is normally case-insensitive - the "Try It" simulator matches case-insensitively too.
import type { CalcOperand, CalcTextPart, CalcTextStep, CalcTextStepKind, CalculatedField, DataColumn, ModelKey } from "../types";
import type { ReportLogicContext } from "./context";
import { allColumnsForModel, columnMatchesKey, isCalculatedColumn } from "./catalogResolution";
import { columnDataTypeCategory } from "./operatorCatalog";
import { sqlLiteral } from "./sqlExpressions";

export const MAX_TEXT_PARTS = 6;
export const MAX_TEXT_STEPS = 8;
export const MAX_CALC_TEXT_LENGTH = 200;
export const MAX_TEXT_SEPARATOR_LENGTH = 20;
export const MAX_TEXT_COUNT = 4000;
export const MAX_TEXT_PART_INDEX = 20;
const MAX_FORMAT_DECIMALS = 6;
const CALC_TEXT = "NVARCHAR(4000)";
const CALC_DECIMAL = "DECIMAL(38, 6)";

// --- Step catalog ---------------------------------------------------------------------

export type TextStepParam = { label: string; min?: number; max?: number; placeholder?: string; allowEmpty?: boolean; maxLength?: number };
export interface TextStepDef {
    key: CalcTextStepKind;
    label: string;
    group: "Clean Up" | "Change Case" | "Extract" | "Replace" | "Pad & Mask" | "Add & Fill" | "Format";
    hint: string;
    text?: TextStepParam;
    text2?: TextStepParam;
    number?: TextStepParam;
    number2?: TextStepParam;
    styles?: { key: string; label: string }[];
}

export const TEXT_DATE_STYLES: { key: string; label: string; sql: (d: string) => string }[] = [
    { key: "iso_date", label: "2026-01-31", sql: (d) => `CONVERT(NVARCHAR(10), ${d}, 23)` },
    { key: "us_date", label: "01/31/2026", sql: (d) => `CONVERT(NVARCHAR(10), ${d}, 101)` },
    { key: "uk_date", label: "31/01/2026", sql: (d) => `CONVERT(NVARCHAR(10), ${d}, 103)` },
    { key: "iso_datetime", label: "2026-01-31 14:05", sql: (d) => `CONVERT(NVARCHAR(16), ${d}, 120)` },
    { key: "time", label: "14:05", sql: (d) => `CONVERT(NVARCHAR(5), ${d}, 108)` },
    { key: "year_month", label: "2026-01", sql: (d) => `CONVERT(NVARCHAR(7), ${d}, 23)` },
    { key: "month_year", label: "Jan 2026", sql: (d) => `FORMAT(${d}, N'MMM yyyy', 'en-US')` },
];

const NUMBER_STYLES = [
    { key: "grouped", label: "1,234.50" },
    { key: "plain", label: "1234.50" },
];

const FIND: TextStepParam = { label: "Text", placeholder: "e.g. -", maxLength: MAX_CALC_TEXT_LENGTH };
const COUNT = (label: string, min = 1): TextStepParam => ({ label, min, max: MAX_TEXT_COUNT });
const CHAR: TextStepParam = { label: "Character", placeholder: "0", maxLength: 1 };

export const TEXT_STEPS: TextStepDef[] = [
    { key: "trim", label: "Trim Spaces", group: "Clean Up", hint: "Removes spaces at the start and end." },
    { key: "trim_start", label: "Trim Leading Spaces", group: "Clean Up", hint: "Removes spaces at the start." },
    { key: "trim_end", label: "Trim Trailing Spaces", group: "Clean Up", hint: "Removes spaces at the end." },
    { key: "collapse_spaces", label: "Collapse Repeated Spaces", group: "Clean Up", hint: "Turns every run of spaces into one space." },
    { key: "upper", label: "UPPERCASE", group: "Change Case", hint: "All letters upper case." },
    { key: "lower", label: "lowercase", group: "Change Case", hint: "All letters lower case." },
    { key: "capitalize", label: "Capitalize First Letter", group: "Change Case", hint: "First letter upper case, the rest lower case." },
    { key: "left", label: "First N Characters", group: "Extract", hint: "Keeps the first N characters.", number: COUNT("Characters") },
    { key: "right", label: "Last N Characters", group: "Extract", hint: "Keeps the last N characters.", number: COUNT("Characters") },
    { key: "substring", label: "Characters From Position", group: "Extract", hint: "Keeps N characters starting at a position (1 = first character).", number: COUNT("Start At"), number2: COUNT("Characters") },
    { key: "before", label: "Text Before", group: "Extract", hint: "Keeps what comes before the first match. Not found: the whole value.", text: FIND },
    { key: "after", label: "Text After", group: "Extract", hint: "Keeps what comes after the first match. Not found: blank.", text: FIND },
    { key: "before_last", label: "Text Before Last", group: "Extract", hint: "Keeps what comes before the last match. Not found: the whole value.", text: FIND },
    { key: "after_last", label: "Text After Last", group: "Extract", hint: "Keeps what comes after the last match. Not found: blank.", text: FIND },
    { key: "part", label: "Part Of Delimited Text", group: "Extract", hint: "Splits on a separator and keeps one part (1 = first). Missing part: blank.", text: { ...FIND, label: "Separator", placeholder: "e.g. ," }, number: { label: "Part", min: 1, max: MAX_TEXT_PART_INDEX } },
    { key: "replace", label: "Replace", group: "Replace", hint: "Replaces every match.", text: { ...FIND, label: "Find" }, text2: { label: "Replace With", placeholder: "(nothing)", allowEmpty: true, maxLength: MAX_CALC_TEXT_LENGTH } },
    { key: "remove", label: "Remove", group: "Replace", hint: "Removes every match.", text: FIND },
    { key: "reverse", label: "Reverse", group: "Replace", hint: "Writes the characters in reverse order." },
    { key: "pad_left", label: "Pad On The Left", group: "Pad & Mask", hint: "Adds the character on the left up to a length, e.g. 42 -> 00042. Longer values are kept as they are.", number: COUNT("Length"), text: CHAR },
    { key: "pad_right", label: "Pad On The Right", group: "Pad & Mask", hint: "Adds the character on the right up to a length. Longer values are kept as they are.", number: COUNT("Length"), text: CHAR },
    { key: "mask", label: "Mask All But Last N", group: "Pad & Mask", hint: "Hides every character except the last N, e.g. *****6789.", number: COUNT("Keep Last", 0), text: { ...CHAR, placeholder: "*" } },
    { key: "prefix", label: "Add Text Before", group: "Add & Fill", hint: "Adds text in front. A blank value stays blank.", text: { ...FIND, placeholder: "e.g. ID-" } },
    { key: "suffix", label: "Add Text After", group: "Add & Fill", hint: "Adds text at the end. A blank value stays blank.", text: { ...FIND, placeholder: "e.g.  mi" } },
    { key: "if_blank", label: "If Blank, Use", group: "Add & Fill", hint: "Uses this text when the value is blank or only spaces.", text: { ...FIND, label: "Value", placeholder: "e.g. Unknown" } },
    { key: "format_number", label: "Format As Number", group: "Format", hint: "Writes a number with fixed decimals. Values that are not numbers become blank.", number: { label: "Decimals", min: 0, max: MAX_FORMAT_DECIMALS }, styles: NUMBER_STYLES },
    { key: "format_date", label: "Format As Date", group: "Format", hint: "Writes a date or time in the chosen format. Values that are not dates become blank.", styles: TEXT_DATE_STYLES.map(({ key, label }) => ({ key, label })) },
];

export function textStepDef(kind: CalcTextStepKind | string | undefined) {
    return TEXT_STEPS.find((step) => step.key === kind) || null;
}

/** A new step with sensible defaults for its parameters. */
export function defaultTextStep(id: string, kind: CalcTextStepKind): CalcTextStep {
    const step: CalcTextStep = { id, kind };
    if (kind === "pad_left" || kind === "pad_right") return { ...step, number: 5, text: "0" };
    if (kind === "mask") return { ...step, number: 4, text: "*" };
    if (kind === "left" || kind === "right") return { ...step, number: 3 };
    if (kind === "substring") return { ...step, number: 1, number2: 3 };
    if (kind === "part") return { ...step, text: ",", number: 1 };
    if (kind === "replace") return { ...step, text: "", text2: "" };
    if (kind === "format_number") return { ...step, number: 2, style: "grouped" };
    if (kind === "format_date") return { ...step, style: "iso_date" };
    return step;
}

// --- Columns -------------------------------------------------------------------------

/** A column a text field may read: visible and not sensitive, any type. Mirrors IsCalcTextColumn. */
export function isCalcTextColumn(column?: DataColumn | null) {
    // A calculated field is never an input of another one (logic/calculatedColumns.ts).
    return !!column && column.isSensitive !== true && !isCalculatedColumn(column);
}

/** A text-typed column "Text As Number" may convert. Mirrors IsCalcTextOnlyColumn. */
export function isCalcTextOnlyColumn(column?: DataColumn | null) {
    return isCalcTextColumn(column) && columnDataTypeCategory(column) === "text";
}

export function calcTextColumns(context: ReportLogicContext, modelKey: ModelKey | "") {
    return modelKey ? allColumnsForModel(context, modelKey).filter(isCalcTextColumn) : [];
}

export function calcTextOnlyColumns(context: ReportLogicContext, modelKey: ModelKey | "") {
    return modelKey ? allColumnsForModel(context, modelKey).filter(isCalcTextOnlyColumn) : [];
}

function resolveFrom(columns: DataColumn[], key: string | undefined) {
    return key ? columns.find((column) => columnMatchesKey(column, key)) || null : null;
}

/** The column as NVARCHAR text. Mirrors ColumnAsTextSql. */
export function columnAsTextSql(column: DataColumn) {
    if (columnDataTypeCategory(column) === "date") {
        const isTime = String(column.dataType || "").trim().toLowerCase().replace(/\(.*$/, "") === "time";
        return `CONVERT(${CALC_TEXT}, ${column.sql}, ${isTime ? 108 : 120})`;
    }
    return `CONVERT(${CALC_TEXT}, ${column.sql})`;
}

const nText = (value: string) => `N${sqlLiteral(value)}`;
const reverseText = (value: string) => value.split("").reverse().join("");

function isInt(value: unknown, min: number, max: number): value is number {
    return typeof value === "number" && Number.isInteger(value) && value >= min && value <= max;
}

// --- Step SQL ---------------------------------------------------------------------------

const reuse = (input: string, body: (v: string) => string) => `(SELECT ${body("rc_v")} FROM (VALUES (${input})) AS rc_t(rc_v))`;
const textLength = (v: string) => `(DATALENGTH(${v}) / 2)`;

// Part n of text split on a separator (see PartSql in the C# file): the separator is appended
// n times, each copy after a private-use character (U+E000) so the end of the value can't
// combine with a copy into a false match; that character is removed from the part afterwards.
const PART_SENTINEL = "NCHAR(57344)";
function partSql(v: string, d: string, length: number, n: number) {
    const padded = `(${v} + REPLICATE(${PART_SENTINEL} + ${d}, ${n}))`;
    const positions = [`CHARINDEX(${d}, ${padded})`];
    for (let k = 2; k <= n; k++) positions.push(`CHARINDEX(${d}, ${padded}, ${positions[k - 2]} + ${length})`);
    const start = n === 1 ? "1" : `(${positions[n - 2]} + ${length})`;
    return `NULLIF(REPLACE(SUBSTRING(${padded}, ${start}, ${positions[n - 1]} - ${start}), ${PART_SENTINEL}, N''), N'')`;
}

/** Why a step can't produce SQL, or null. Same limits as the server. */
export function textStepIssue(step: CalcTextStep): string | null {
    const def = textStepDef(step.kind);
    if (!def) return "choose a step.";
    const text = step.text ?? "";
    const text2 = step.text2 ?? "";
    if (def.text) {
        const max = def.text.maxLength ?? MAX_CALC_TEXT_LENGTH;
        if (!text.length) return `fill in "${def.text.label}".`;
        if (text.length > max) return `"${def.text.label}" must be ${max} character${max === 1 ? "" : "s"} or fewer.`;
    }
    if (def.text2 && text2.length > (def.text2.maxLength ?? MAX_CALC_TEXT_LENGTH)) return `"${def.text2.label}" is too long.`;
    if (def.number) {
        const min = def.number.min ?? 1;
        const max = def.number.max ?? MAX_TEXT_COUNT;
        const value = step.kind === "format_number" ? step.number ?? 0 : step.number;
        if (!isInt(value, min, max)) return `"${def.number.label}" must be a whole number from ${min} to ${max}.`;
    }
    if (def.number2 && !isInt(step.number2, def.number2.min ?? 1, def.number2.max ?? MAX_TEXT_COUNT)) {
        return `"${def.number2.label}" must be a whole number from ${def.number2.min ?? 1} to ${def.number2.max ?? MAX_TEXT_COUNT}.`;
    }
    if (step.kind === "format_date" && !TEXT_DATE_STYLES.some((style) => style.key === step.style)) return "choose a date format.";
    return null;
}

/** One step on `input`. Mirrors TextStepSql; null when the step is incomplete. */
export function textStepSql(step: CalcTextStep, input: string): string | null {
    if (textStepIssue(step)) return null;
    const text = step.text ?? "";
    const n = step.number ?? 0;
    switch (step.kind) {
        case "trim": return `LTRIM(RTRIM(${input}))`;
        case "trim_start": return `LTRIM(${input})`;
        case "trim_end": return `RTRIM(${input})`;
        case "collapse_spaces": return `REPLACE(REPLACE(REPLACE(${input}, N' ', NCHAR(17) + NCHAR(18)), NCHAR(18) + NCHAR(17), N''), NCHAR(17) + NCHAR(18), N' ')`;
        case "upper": return `UPPER(${input})`;
        case "lower": return `LOWER(${input})`;
        case "capitalize": return reuse(input, (v) => `UPPER(LEFT(${v}, 1)) + LOWER(SUBSTRING(${v}, 2, ${MAX_TEXT_COUNT}))`);
        case "left": return `LEFT(${input}, ${n})`;
        case "right": return `RIGHT(${input}, ${n})`;
        case "substring": return `SUBSTRING(${input}, ${n}, ${step.number2})`;
        case "before": {
            const d = nText(text);
            return reuse(input, (v) => `CASE WHEN CHARINDEX(${d}, ${v}) > 0 THEN LEFT(${v}, CHARINDEX(${d}, ${v}) - 1) ELSE ${v} END`);
        }
        case "after": {
            const d = nText(text);
            return reuse(input, (v) => `CASE WHEN CHARINDEX(${d}, ${v}) > 0 THEN SUBSTRING(${v}, CHARINDEX(${d}, ${v}) + ${text.length}, ${MAX_TEXT_COUNT}) END`);
        }
        case "before_last": {
            const r = nText(reverseText(text));
            return reuse(input, (v) => `CASE WHEN CHARINDEX(${r}, REVERSE(${v})) > 0 THEN LEFT(${v}, ${textLength(v)} - CHARINDEX(${r}, REVERSE(${v})) - ${text.length} + 1) ELSE ${v} END`);
        }
        case "after_last": {
            const r = nText(reverseText(text));
            return reuse(input, (v) => `CASE WHEN CHARINDEX(${r}, REVERSE(${v})) > 0 THEN RIGHT(${v}, CHARINDEX(${r}, REVERSE(${v})) - 1) END`);
        }
        case "part": {
            const d = nText(text);
            return reuse(input, (v) => partSql(v, d, text.length, n));
        }
        case "replace": return `REPLACE(${input}, ${nText(text)}, ${nText(step.text2 ?? "")})`;
        case "remove": return `REPLACE(${input}, ${nText(text)}, N'')`;
        case "pad_left":
        case "pad_right": {
            const c = nText(text);
            return reuse(input, (v) => step.kind === "pad_left"
                ? `CASE WHEN ${textLength(v)} >= ${n} THEN ${v} ELSE RIGHT(REPLICATE(${c}, ${n}) + ${v}, ${n}) END`
                : `CASE WHEN ${textLength(v)} >= ${n} THEN ${v} ELSE LEFT(${v} + REPLICATE(${c}, ${n}), ${n}) END`);
        }
        case "mask": {
            const c = nText(text);
            return reuse(input, (v) => `CASE WHEN ${textLength(v)} <= ${n} THEN ${v} ELSE REPLICATE(${c}, ${textLength(v)} - ${n}) + RIGHT(${v}, ${n}) END`);
        }
        case "reverse": return `REVERSE(${input})`;
        case "prefix": return `${nText(text)} + ${input}`;
        case "suffix": return `${input} + ${nText(text)}`;
        case "if_blank": return `COALESCE(NULLIF(${input}, N''), ${nText(text)})`;
        case "format_number": return `FORMAT(TRY_CONVERT(${CALC_DECIMAL}, ${input}), N'${step.style === "plain" ? "F" : "N"}${n}', 'en-US')`;
        case "format_date": return TEXT_DATE_STYLES.find((style) => style.key === step.style)!.sql(`TRY_CONVERT(DATETIME2, ${input})`);
        default: return null;
    }
}

// --- Field SQL ----------------------------------------------------------------------------

function partSourceSql(context: ReportLogicContext, field: CalculatedField, part: CalcTextPart): string | null {
    if (part.sourceKind === "text") {
        const value = part.value ?? "";
        return value.length && value.length <= MAX_CALC_TEXT_LENGTH ? nText(value) : null;
    }
    const column = resolveFrom(calcTextColumns(context, field.model), part.column);
    return column ? columnAsTextSql(column) : null;
}

function textPartSql(context: ReportLogicContext, field: CalculatedField, part: CalcTextPart): string | null {
    if ((part.steps || []).length > MAX_TEXT_STEPS) return null;
    let sql = partSourceSql(context, field, part);
    for (const step of part.steps || []) {
        if (!sql) return null;
        sql = textStepSql(step, sql);
    }
    return sql;
}

/** The SQL of a text field (NVARCHAR, blank when empty), or null when incomplete. Mirrors TextCalculatedFieldSql. */
export function textFieldSql(context: ReportLogicContext, field: CalculatedField): string | null {
    const parts = field.textParts || [];
    if (!parts.length || parts.length > MAX_TEXT_PARTS) return null;
    const separator = field.textSeparator ?? "";
    if (separator.length > MAX_TEXT_SEPARATOR_LENGTH) return null;
    const sqls = parts.map((part) => textPartSql(context, field, part));
    if (sqls.some((sql) => !sql)) return null;
    const pieces = sqls as string[];
    let joined: string;
    if (!separator.length) joined = pieces.length === 1 ? pieces[0] : `CONCAT(${pieces.join(", ")})`;
    else if (pieces.length === 1) joined = pieces[0];
    else {
        const sep = nText(separator);
        joined = `STUFF(CONCAT(${pieces.map((piece) => `${sep} + NULLIF(${piece}, N'')`).join(", ")}), 1, ${separator.length}, N'')`;
    }
    return `NULLIF(${joined}, N'')`;
}

// --- Text -> number operands (number fields) --------------------------------------------

export function isTextOperand(operand: CalcOperand): operand is Extract<CalcOperand, { kind: "text_length" | "text_position" | "text_number" }> {
    return operand.kind === "text_length" || operand.kind === "text_position" || operand.kind === "text_number";
}

function textOperandColumn(context: ReportLogicContext, field: CalculatedField, operand: CalcOperand) {
    if (!isTextOperand(operand)) return null;
    const columns = operand.kind === "text_number" ? calcTextOnlyColumns(context, field.model) : calcTextColumns(context, field.model);
    return resolveFrom(columns, operand.column);
}

/** Mirrors TextOperandSql; null when incomplete. */
export function textOperandSql(context: ReportLogicContext, field: CalculatedField, operand: CalcOperand): string | null {
    const column = textOperandColumn(context, field, operand);
    if (!column) return null;
    const text = columnAsTextSql(column);
    let sql: string;
    if (operand.kind === "text_length") sql = `CONVERT(${CALC_DECIMAL}, LEN(${text}))`;
    else if (operand.kind === "text_position") {
        const find = operand.value ?? "";
        if (!find.length || find.length > MAX_CALC_TEXT_LENGTH) return null;
        sql = `CONVERT(${CALC_DECIMAL}, CHARINDEX(${nText(find)}, ${text}))`;
    } else sql = `TRY_CONVERT(${CALC_DECIMAL}, REPLACE(REPLACE(LTRIM(RTRIM(${text})), N',', N''), N'$', N''))`;
    return field.blankAs === "zero" ? `ISNULL(${sql}, 0)` : sql;
}

export function textOperandIssue(context: ReportLogicContext, field: CalculatedField, operand: CalcOperand): string | null {
    if (!isTextOperand(operand)) return null;
    if (!operand.column) return operand.kind === "text_number" ? "choose a text column." : "choose a column.";
    if (!textOperandColumn(context, field, operand)) return operand.kind === "text_number" ? "that column is not a usable text column on this data model." : "that column is not usable on this data model.";
    if (operand.kind === "text_position" && !(operand.value ?? "").length) return "enter the text to find.";
    if (operand.kind === "text_position" && (operand.value ?? "").length > MAX_CALC_TEXT_LENGTH) return `the text to find must be ${MAX_CALC_TEXT_LENGTH} characters or fewer.`;
    return null;
}

export function textOperandText(context: ReportLogicContext, field: CalculatedField, operand: CalcOperand) {
    const label = textOperandColumn(context, field, operand)?.label || "?";
    if (operand.kind === "text_length") return `length of ${label}`;
    if (operand.kind === "text_position") return `position of "${operand.value || "?"}" in ${label}`;
    return `${label} as a number`;
}

// --- Validation and plain English ---------------------------------------------------------

export function textFieldIssues(context: ReportLogicContext, field: CalculatedField): string[] {
    const issues: string[] = [];
    const parts = field.textParts || [];
    if (!parts.length) issues.push("Add at least one piece to the text.");
    if (parts.length > MAX_TEXT_PARTS) issues.push(`A text field can have at most ${MAX_TEXT_PARTS} pieces.`);
    if ((field.textSeparator ?? "").length > MAX_TEXT_SEPARATOR_LENGTH) issues.push(`The separator must be ${MAX_TEXT_SEPARATOR_LENGTH} characters or fewer.`);
    if (!field.model) return issues;
    const columns = calcTextColumns(context, field.model);
    parts.forEach((part, index) => {
        const label = `Piece ${index + 1}`;
        if (part.sourceKind === "text") {
            if (!(part.value ?? "").length) issues.push(`${label}: type the text.`);
            else if ((part.value ?? "").length > MAX_CALC_TEXT_LENGTH) issues.push(`${label}: the text must be ${MAX_CALC_TEXT_LENGTH} characters or fewer.`);
        } else if (!part.column) issues.push(`${label}: choose a column.`);
        else if (!resolveFrom(columns, part.column)) issues.push(`${label}: that column is not usable on this data model.`);
        if ((part.steps || []).length > MAX_TEXT_STEPS) issues.push(`${label}: at most ${MAX_TEXT_STEPS} steps.`);
        (part.steps || []).forEach((step, stepIndex) => {
            const issue = textStepIssue(step);
            if (issue) issues.push(`${label}, step ${stepIndex + 1}: ${issue}`);
        });
    });
    return issues;
}

function stepText(step: CalcTextStep) {
    const def = textStepDef(step.kind);
    if (!def) return "?";
    const quote = (value?: string) => `"${value ?? ""}"`;
    switch (step.kind) {
        case "left": return `first ${step.number ?? "?"} characters`;
        case "right": return `last ${step.number ?? "?"} characters`;
        case "substring": return `${step.number2 ?? "?"} characters from position ${step.number ?? "?"}`;
        case "before": case "after": case "before_last": case "after_last": return `${def.label.toLowerCase()} ${quote(step.text)}`;
        case "part": return `part ${step.number ?? "?"} split on ${quote(step.text)}`;
        case "replace": return `replace ${quote(step.text)} with ${quote(step.text2)}`;
        case "remove": return `remove ${quote(step.text)}`;
        case "pad_left": case "pad_right": return `${def.label.toLowerCase()} to ${step.number ?? "?"} with ${quote(step.text)}`;
        case "mask": return `mask all but last ${step.number ?? "?"} with ${quote(step.text)}`;
        case "prefix": return `add ${quote(step.text)} before`;
        case "suffix": return `add ${quote(step.text)} after`;
        case "if_blank": return `if blank use ${quote(step.text)}`;
        case "format_number": return `format as number with ${step.number ?? 0} decimals`;
        case "format_date": return `format as date (${TEXT_DATE_STYLES.find((style) => style.key === step.style)?.label || "?"})`;
        default: return def.label.toLowerCase() === "uppercase" ? "UPPERCASE" : def.label.toLowerCase();
    }
}

/** e.g. "Last Name (trim, UPPERCASE) + ", " + First Name (capitalize first letter)". */
export function textFieldEnglish(context: ReportLogicContext, field: CalculatedField) {
    const columns = calcTextColumns(context, field.model);
    const pieces = (field.textParts || []).map((part) => {
        const source = part.sourceKind === "text" ? `"${part.value ?? ""}"` : resolveFrom(columns, part.column)?.label || "?";
        const steps = (part.steps || []).map(stepText);
        return steps.length ? `${source} (${steps.join(", ")})` : source;
    });
    if (!pieces.length) return "";
    const separator = field.textSeparator ?? "";
    return pieces.join(separator ? ` + "${separator}" + ` : " + ") + (separator && pieces.length > 1 ? ", skipping blank pieces" : "");
}

// --- "Try It" simulator -------------------------------------------------------------------

const indexOfCi = (haystack: string, needle: string, from = 0) => haystack.toLowerCase().indexOf(needle.toLowerCase(), from);
const lastIndexOfCi = (haystack: string, needle: string) => haystack.toLowerCase().lastIndexOf(needle.toLowerCase());
const isBlank = (value: string | null) => value === null || value.replace(/ +$/, "") === "";

function replaceCi(value: string, find: string, replacement: string) {
    if (!find) return value;
    let result = "";
    let from = 0;
    for (let index = indexOfCi(value, find); index >= 0; index = indexOfCi(value, find, from)) {
        result += value.slice(from, index) + replacement;
        from = index + find.length;
    }
    return result + value.slice(from);
}

function parseSampleDate(value: string) {
    const match = value.trim().match(/^(?:(\d{4})-(\d{2})-(\d{2}))?[ T]?(?:(\d{1,2}):(\d{2})(?::(\d{2}))?)?$/);
    if (!match || (!match[1] && !match[4])) return null;
    return { y: match[1] ? Number(match[1]) : 1900, m: match[2] ? Number(match[2]) : 1, d: match[3] ? Number(match[3]) : 1, h: Number(match[4] || 0), mi: Number(match[5] || 0) };
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const two = (value: number) => String(value).padStart(2, "0");

function simulateStep(step: CalcTextStep, value: string | null): string | null {
    const text = step.text ?? "";
    const n = step.number ?? 0;
    if (step.kind === "if_blank") return isBlank(value) ? text : value;
    if (value === null) return null;
    switch (step.kind) {
        case "trim": return value.replace(/^ +| +$/g, "");
        case "trim_start": return value.replace(/^ +/, "");
        case "trim_end": return value.replace(/ +$/, "");
        case "collapse_spaces": return value.replace(/ {2,}/g, " ");
        case "upper": return value.toUpperCase();
        case "lower": return value.toLowerCase();
        case "capitalize": return value.slice(0, 1).toUpperCase() + value.slice(1).toLowerCase();
        case "left": return value.slice(0, n);
        case "right": return n ? value.slice(-n) : "";
        case "substring": return value.substr(n - 1, step.number2 ?? 0);
        case "before": { const at = indexOfCi(value, text); return at >= 0 ? value.slice(0, at) : value; }
        case "after": { const at = indexOfCi(value, text); return at >= 0 ? value.slice(at + text.length) : null; }
        case "before_last": { const at = lastIndexOfCi(value, text); return at >= 0 ? value.slice(0, at) : value; }
        case "after_last": { const at = lastIndexOfCi(value, text); return at >= 0 ? value.slice(at + text.length) : null; }
        case "part": {
            const parts: string[] = [];
            let from = 0;
            for (let at = indexOfCi(value, text); at >= 0; at = indexOfCi(value, text, from)) { parts.push(value.slice(from, at)); from = at + text.length; }
            parts.push(value.slice(from));
            // NULLIF(part, N'') in the SQL: an empty or spaces-only part is blank.
            const piece = parts[n - 1];
            return piece === undefined || isBlank(piece) ? null : piece;
        }
        case "replace": return replaceCi(value, text, step.text2 ?? "");
        case "remove": return replaceCi(value, text, "");
        case "pad_left": return value.length >= n ? value : (text.repeat(n) + value).slice(-n);
        case "pad_right": return value.length >= n ? value : (value + text.repeat(n)).slice(0, n);
        case "mask": return value.length <= n ? value : text.repeat(value.length - n) + (n ? value.slice(-n) : "");
        case "reverse": return value.split("").reverse().join("");
        case "prefix": return text + value;
        case "suffix": return value + text;
        case "format_number": {
            const raw = value.trim();
            if (!/^[-+]?(\d+\.?\d*|\.\d+)$/.test(raw)) return null;
            return Number(raw).toLocaleString("en-US", { minimumFractionDigits: n, maximumFractionDigits: n, useGrouping: step.style !== "plain" });
        }
        case "format_date": {
            const d = parseSampleDate(value);
            if (!d) return null;
            switch (step.style) {
                case "iso_date": return `${d.y}-${two(d.m)}-${two(d.d)}`;
                case "us_date": return `${two(d.m)}/${two(d.d)}/${d.y}`;
                case "uk_date": return `${two(d.d)}/${two(d.m)}/${d.y}`;
                case "iso_datetime": return `${d.y}-${two(d.m)}-${two(d.d)} ${two(d.h)}:${two(d.mi)}`;
                case "time": return `${two(d.h)}:${two(d.mi)}`;
                case "year_month": return `${d.y}-${two(d.m)}`;
                case "month_year": return `${MONTHS[d.m - 1]} ${d.y}`;
                default: return null;
            }
        }
        default: return value;
    }
}

/** Evaluates a text field on sample values (keyed by column key), as the SQL would. */
export function simulateTextField(context: ReportLogicContext, field: CalculatedField, inputs: Record<string, string>): string {
    const columns = calcTextColumns(context, field.model);
    const pieces = (field.textParts || []).map((part) => {
        let value: string | null;
        if (part.sourceKind === "text") value = part.value ?? "";
        else {
            const column = resolveFrom(columns, part.column);
            const raw = column ? inputs[column.key] ?? "" : "";
            value = raw === "" ? null : raw;
        }
        for (const step of part.steps || []) value = simulateStep(step, value);
        return value;
    });
    const separator = field.textSeparator ?? "";
    const result = separator && pieces.length > 1
        ? pieces.filter((piece) => !isBlank(piece)).join(separator)
        : pieces.map((piece) => piece ?? "").join("");
    // NULLIF(result, N'') is also NULL for spaces only (SQL Server ignores trailing spaces when comparing).
    return isBlank(result) ? "(blank)" : result;
}

/** A text -> number operand on a sample value, as the SQL would (null = blank). */
export function simulateTextOperand(context: ReportLogicContext, field: CalculatedField, operand: CalcOperand, inputs: Record<string, string>): number | null {
    const column = textOperandColumn(context, field, operand);
    const raw = column ? inputs[column.key] ?? "" : "";
    if (raw === "") return null;
    if (operand.kind === "text_length") return raw.replace(/ +$/, "").length;
    if (operand.kind === "text_position") return indexOfCi(raw, operand.value ?? "") + 1;
    const cleaned = raw.trim().replace(/[,$]/g, "");
    return /^[-+]?(\d+\.?\d*|\.\d+)$/.test(cleaned) ? Number(cleaned) : null;
}

/** Columns a text field (or text operands) need a sample for, in order. */
export function textFieldInputColumns(context: ReportLogicContext, field: CalculatedField): DataColumn[] {
    const columns = calcTextColumns(context, field.model);
    return (field.textParts || []).filter((part) => part.sourceKind === "column").map((part) => resolveFrom(columns, part.column)).filter(Boolean) as DataColumn[];
}

export function textOperandInputColumn(context: ReportLogicContext, field: CalculatedField, operand: CalcOperand) {
    return textOperandColumn(context, field, operand);
}
